import { FC, Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';

// Static imports
import { PrivateRoute } from './PrivateRoute';
import { WbFallback } from 'components/wb-fallback';
import ContactsImport from 'modules/Login/Welcome/Components/ContactsImport';
import PreferenceManagement from '../../src/modules/Preference/PreferenceManagement';
import UnsubscribePage from '../../src/modules/Preference/Components/UnsubscribePage';
// Lazy imports
const SSO = lazy(() => import('modules/Login/Access/Components/SSO'));
const Content = lazy(() => import('modules/Content/Shared/ContentTab'));
const Login = lazy(() => import('modules/Login/Access/Components/Login'));
const SignUpPage = lazy(() => import('modules/Login/Access/Components/SignUpPage'));
const ResetPassword = lazy(() => import('modules/Login/Access/Components/ResetPassword'));
const LoginRedirect = lazy(() => import('modules/Login/Access/Components/LoginRedirect'));
const PermissionDenied = lazy(() => import('modules/Campaigns/Shared/PermissionDenied'));
const WelcomeAnimation = lazy(() => import('modules/Common/Components/WelcomeAnimation'));
const CreateCampaign = lazy(() => import('modules/Campaigns/CampaignContent/Components/CreateCampaign'));
const EmailWithOutlookRedirect = lazy(() => import('modules/Publish/Components/EmailWithOutlookRedirect'));
const UnSubSorryPage = lazy(() => import('modules/Preference/Components/UnSubSorryPage'));
const WbDesignSystem = lazy(() => import('modules/wb-design-system'));

export const WbRoutes: FC = () => {
  const { NODE_ENV, REACT_APP_LINK } = process.env;
  const isDevelopment = NODE_ENV === 'development' || REACT_APP_LINK === 'https://qawb.xiqonline.com/';
  return (
    <Suspense fallback={<WbFallback />}>
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/resetPassword/:resetKey?" element={<ResetPassword />} />
        <Route path="/login-redirect" element={<LoginRedirect />} />
        <Route path="/sso/:csrfToken/" element={<SSO />} />
        <Route path="/unsub-success" element={<PreferenceManagement />} />
        {/* i2c unsub and resub routes */}
        <Route path="/unsub/i2c/:unsubid" element={<UnsubscribePage />} />
        <Route path="/resub/i2c/:unsubid" element={<UnSubSorryPage />} />
        <Route path="/unsub/:unsubid" element={<PreferenceManagement />} />
        {/* the following route is only to be used for an issue in i2c email on Tuesday May 9, 2023 */}
        <Route path="/unsub/" element={<UnsubscribePage />} />
        {/* Following are the logged-in users routes */}
        <Route
          path="/permission_denied"
          element={
            <PrivateRoute>
              <PermissionDenied />
            </PrivateRoute>
          }
        />
        <Route
          path="/import"
          element={
            <PrivateRoute>
              <ContactsImport />
            </PrivateRoute>
          }
        />
        <Route
          path="/welcome"
          element={
            <PrivateRoute>
              <WelcomeAnimation />
            </PrivateRoute>
          }
        />
        <Route
          path="/content/:tab"
          element={
            <PrivateRoute>
              <Content />
            </PrivateRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PrivateRoute>
              <SignUpPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/campaigns/:page?/:tab?/:subTab?"
          element={
            <PrivateRoute>
              <Content />
            </PrivateRoute>
          }
        />
        <Route
          path="/createcampaign"
          element={
            <PrivateRoute>
              <CreateCampaign />
            </PrivateRoute>
          }
        />
        {/* <PrivateRoute path="/audiences/:page?/:tab?/:subTab?" component={Content} exact /> */}
        <Route
          path="/audiences/:page?/:tab?/:subTab?"
          element={
            <PrivateRoute>
              <Content />
            </PrivateRoute>
          }
        />
        <Route
          path="/analytics/:page?/:tab?"
          element={
            <PrivateRoute>
              <Content />
            </PrivateRoute>
          }
        />
        <Route
          path="/buyer-journey/:tab?"
          element={
            <PrivateRoute>
              <Content />
            </PrivateRoute>
          }
        />
        <Route
          path="/outlook_auth"
          element={
            <PrivateRoute>
              <EmailWithOutlookRedirect />
            </PrivateRoute>
          }
        />
        <Route
          path="/automation/:page?/:tab?"
          element={
            <PrivateRoute>
              <Content />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/:page?/:tab?"
          element={
            <PrivateRoute>
              <Content />
            </PrivateRoute>
          }
        />
        {isDevelopment && <Route path="/design-system" element={WbDesignSystem} />}
      </Routes>
    </Suspense>
  );
};
